import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Mic, ShieldAlert, Trash2, Upload, X } from "lucide-react";
import type { UppyFile, UploadResult } from "@uppy/core";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ObjectUploader } from "@/components/ObjectUploader";

type UppyMeta = Record<string, unknown> & { objectPath?: string };
type UppyBody = Record<string, unknown>;
type VoiceUppyFile = UppyFile<UppyMeta, UppyBody>;
type VoiceUploadResult = UploadResult<UppyMeta, UppyBody>;

const MIN_SAMPLES = 3;
const MAX_SAMPLES = 10;
const ACCEPTED_AUDIO_TYPES = [
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/wave",
  "audio/webm",
  "audio/ogg",
  "audio/mp4",
  "audio/m4a",
  "audio/x-m4a",
  "audio/aac",
  "audio/flac",
];

type VoiceProfile = {
  id: string;
  status: "draft" | "training" | "ready" | "failed" | "revoked";
  providerVoiceId: string | null;
  displayName: string | null;
  trainingSampleUrls: string[];
  consentRecordingUrl: string | null;
  consentAttestedBy: string | null;
  consentAttestedAt: string | null;
  lastProviderError: string | null;
} | null;

type LiveStatus = {
  ready: boolean;
  failed: boolean;
  errorMessage: string | null;
} | null;

type UploadedClip = {
  objectPath: string;
  fileName: string;
  mimeType: string;
  previewUrl: string;
};

const MAX_BYTES = 25 * 1024 * 1024;

export default function AgencyVoicePage() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{
    profile: VoiceProfile;
    liveStatus: LiveStatus;
    providerKey: string;
  }>({
    queryKey: ["/api/admin/voice-clone"],
  });

  const [displayName, setDisplayName] = useState("");
  const [consentDescription, setConsentDescription] = useState("");
  const [consentClip, setConsentClip] = useState<UploadedClip | null>(null);
  const [sampleClips, setSampleClips] = useState<UploadedClip[]>([]);
  const [consentChecked, setConsentChecked] = useState(false);

  const start = useMutation({
    mutationFn: async () => {
      if (!consentClip) throw new Error("Upload a consent recording first");
      if (sampleClips.length < MIN_SAMPLES) {
        throw new Error(`Upload at least ${MIN_SAMPLES} voice samples`);
      }
      return apiRequest("POST", "/api/admin/voice-clone", {
        displayName,
        consentDescription,
        consentRecordingUrl: consentClip.objectPath,
        sampleUrls: sampleClips.map((c) => c.objectPath),
        consentAttested: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/voice-clone"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/outbound-caller"] });
      toast({ title: "Voice training started" });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to start training",
        description: err?.message ?? "Unknown",
        variant: "destructive",
      });
    },
  });

  const revoke = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/admin/voice-clone/revoke", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/voice-clone"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/outbound-caller"] });
      toast({ title: "Voice profile revoked" });
    },
  });

  const getUploadParameters = async (file: VoiceUppyFile) => {
    const ct = file.type || "application/octet-stream";
    if (!ACCEPTED_AUDIO_TYPES.includes(ct)) {
      throw new Error(`Unsupported file type: ${ct}. Upload an audio file.`);
    }
    const res = await apiRequest("POST", "/api/admin/voice-clone/upload-url", {});
    const data = (await res.json()) as { uploadURL: string; objectPath: string };
    file.meta = { ...(file.meta ?? {}), objectPath: data.objectPath };
    return {
      method: "PUT" as const,
      url: data.uploadURL,
      headers: { "Content-Type": ct },
    };
  };

  const toUploadedClip = (f: VoiceUppyFile): UploadedClip | null => {
    const objectPath = f.meta?.objectPath;
    if (typeof objectPath !== "string" || !objectPath) return null;
    const blob = f.data instanceof Blob ? f.data : undefined;
    const previewUrl = blob ? URL.createObjectURL(blob) : "";
    return {
      objectPath,
      fileName: f.name || "clip",
      mimeType: f.type || "audio/mpeg",
      previewUrl,
    };
  };

  const removeSampleAt = (idx: number) => {
    setSampleClips((prev) => {
      const target = prev[idx];
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearConsent = () => {
    if (consentClip?.previewUrl) URL.revokeObjectURL(consentClip.previewUrl);
    setConsentClip(null);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  const profile = data?.profile ?? null;
  const sampleSlotsLeft = Math.max(0, MAX_SAMPLES - sampleClips.length);
  const samplesShort = sampleClips.length < MIN_SAMPLES;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mic className="h-4 w-4" /> Agency Voice
          </CardTitle>
          <CardDescription>
            One cloned voice per workspace. Used by the AI Outbound Caller to ask leads for
            their best email when Apollo and the email finder couldn't find one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {profile ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <strong>Status:</strong>
                <Badge data-testid="badge-voice-status">{profile.status}</Badge>
                {data?.liveStatus?.failed && (
                  <Badge variant="destructive">provider error</Badge>
                )}
              </div>
              {profile.displayName && (
                <div className="text-sm">
                  <strong>Name:</strong> {profile.displayName}
                </div>
              )}
              {profile.consentAttestedAt && (
                <div className="text-xs text-muted-foreground">
                  Consent attested {new Date(profile.consentAttestedAt).toLocaleString()} by{" "}
                  {profile.consentAttestedBy ?? "—"}
                </div>
              )}
              {profile.lastProviderError && (
                <Alert variant="destructive">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>Last error</AlertTitle>
                  <AlertDescription className="font-mono text-xs">
                    {profile.lastProviderError}
                  </AlertDescription>
                </Alert>
              )}
              {profile.status === "ready" && (
                <Button
                  variant="destructive"
                  onClick={() => revoke.mutate()}
                  disabled={revoke.isPending}
                  data-testid="button-revoke-voice"
                >
                  {revoke.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-1" /> Revoke voice
                    </>
                  )}
                </Button>
              )}
            </div>
          ) : (
            <Alert>
              <Mic className="h-4 w-4" />
              <AlertTitle>No voice profile yet</AlertTitle>
              <AlertDescription>
                Upload a consent recording + 3-10 short voice samples to create your cloned voice.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Train (or re-train) voice</CardTitle>
          <CardDescription>
            Use clean ~30s recordings of the agency owner. Avoid background music. Provider:{" "}
            <span className="font-mono">{data?.providerKey}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="display-name">Display name</Label>
            <Input
              id="display-name"
              data-testid="input-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Sarah's outbound voice"
            />
          </div>
          <div>
            <Label htmlFor="consent-desc">Consent description</Label>
            <Input
              id="consent-desc"
              data-testid="input-consent-description"
              value={consentDescription}
              onChange={(e) => setConsentDescription(e.target.value)}
              placeholder="Sarah, agency owner, recorded 2026-04-30"
            />
          </div>

          <div className="space-y-2">
            <Label>Consent recording</Label>
            {consentClip ? (
              <div
                className="rounded-md border p-3 space-y-2"
                data-testid="card-consent-clip"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium truncate" data-testid="text-consent-filename">
                    {consentClip.fileName}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearConsent}
                    data-testid="button-clear-consent"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {consentClip.previewUrl && (
                  <audio
                    controls
                    src={consentClip.previewUrl}
                    className="w-full"
                    data-testid="audio-consent-preview"
                  />
                )}
              </div>
            ) : (
              <ObjectUploader
                maxNumberOfFiles={1}
                maxFileSize={MAX_BYTES}
                onGetUploadParameters={getUploadParameters}
                onComplete={(result: VoiceUploadResult) => {
                  const f = result.successful?.[0] as VoiceUppyFile | undefined;
                  if (!f) return;
                  const clip = toUploadedClip(f);
                  if (clip) setConsentClip(clip);
                }}
              >
                <span className="inline-flex items-center gap-2" data-testid="button-upload-consent">
                  <Upload className="h-4 w-4" /> Upload consent recording
                </span>
              </ObjectUploader>
            )}
            <p className="text-xs text-muted-foreground">
              A short clip of the speaker stating they consent to having their voice cloned for
              outbound business communications.
            </p>
          </div>

          <div className="space-y-2">
            <Label>
              Voice samples ({sampleClips.length}/{MAX_SAMPLES})
              {samplesShort && (
                <span className="ml-2 text-xs text-muted-foreground" data-testid="text-sample-min-hint">
                  need at least {MIN_SAMPLES}
                </span>
              )}
            </Label>
            {sampleClips.length > 0 && (
              <ul className="space-y-2" data-testid="list-sample-clips">
                {sampleClips.map((clip, idx) => (
                  <li
                    key={clip.objectPath}
                    className="rounded-md border p-3 space-y-2"
                    data-testid={`card-sample-clip-${idx}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium truncate">
                        {idx + 1}. {clip.fileName}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSampleAt(idx)}
                        data-testid={`button-remove-sample-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {clip.previewUrl && (
                      <audio
                        controls
                        src={clip.previewUrl}
                        className="w-full"
                        data-testid={`audio-sample-preview-${idx}`}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {sampleSlotsLeft > 0 && (
              <ObjectUploader
                maxNumberOfFiles={sampleSlotsLeft}
                maxFileSize={MAX_BYTES}
                onGetUploadParameters={getUploadParameters}
                onComplete={(result: VoiceUploadResult) => {
                  const successful = (result.successful ?? []) as VoiceUppyFile[];
                  const newClips = successful
                    .map(toUploadedClip)
                    .filter((c): c is UploadedClip => c !== null);
                  if (newClips.length === 0) return;
                  setSampleClips((prev) => [...prev, ...newClips].slice(0, MAX_SAMPLES));
                }}
              >
                <span className="inline-flex items-center gap-2" data-testid="button-upload-samples">
                  <Upload className="h-4 w-4" />
                  {sampleClips.length === 0
                    ? `Upload voice samples (${MIN_SAMPLES}-${MAX_SAMPLES})`
                    : "Add more samples"}
                </span>
              </ObjectUploader>
            )}
            <p className="text-xs text-muted-foreground">
              {MIN_SAMPLES}-{MAX_SAMPLES} clean ~30s recordings work best. The uploader supports drag-and-drop.
            </p>
          </div>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={consentChecked}
              onCheckedChange={(v) => setConsentChecked(v === true)}
              data-testid="checkbox-consent"
            />
            <span>
              I have explicit consent from the speaker to clone their voice for outbound
              business communications, and I understand this voice will be used by the AI
              outbound caller until I revoke it.
            </span>
          </label>
          <Button
            onClick={() => start.mutate()}
            disabled={
              !consentChecked ||
              !displayName ||
              !consentDescription ||
              !consentClip ||
              sampleClips.length < MIN_SAMPLES ||
              start.isPending
            }
            data-testid="button-start-cloning"
          >
            {start.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start cloning"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
